import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '../../components/services/auth/auth.service';
import { isPlatformBrowser } from '@angular/common';
import { Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(
    private msalService: MsalService,
    private router: Router,
    private authService: AuthService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    
    // If not in browser (SSR), allow access or handle appropriately
    if (!isPlatformBrowser(this.platformId)) {
      // During SSR, we can't check authentication, so we allow access
      // The actual authentication check will happen on the client side
      return true;
    }

    try {
      // Check MSAL authentication and session storage
      const hasAccounts = this.msalService.instance.getAllAccounts().length > 0;
      const hasSessionId = this.getSessionStorageItem('Session_Id');
      const isAuthenticated = hasAccounts && hasSessionId;

      if (!isAuthenticated) {
        this.router.navigate(['/loginpage']);
        return false;
      }

      // Update session timestamp on each navigation
      this.setSessionStorageItem('sessionTimestamp', new Date().getTime().toString());
      return true;

    } catch (error) {
      console.error('Error in AuthGuard:', error);
      this.router.navigate(['/loginpage']);
      return false;
    }
  }

  // Helper method to safely get from sessionStorage
  private getSessionStorageItem(key: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        console.error('Error accessing sessionStorage:', error);
        return null;
      }
    }
    return null;
  }

  // Helper method to safely set to sessionStorage
  private setSessionStorageItem(key: string, value: string): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        console.error('Error setting sessionStorage:', error);
      }
    }
  }
}